/**
 * POS Reports Dashboard
 * =====================
 * 
 * Sales reporting and analytics for the POS system.
 * Optimized for performance with debounced date selection.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  DollarSign,
  Package,
  Users,
  ArrowLeft,
  Download,
  Calendar,
  Loader2,
  Receipt,
  PieChart,
  UserCheck,
  Clock,
  Briefcase,
  CreditCard,
  Banknote,
  Smartphone,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Filter,
  Table2,
  FileText,
  Percent,
  Target,
  Zap,
  Award,
} from "lucide-react";
import { Link } from "react-router-dom";

const POSReportsPage = () => {
  const { token } = useAuth();
  
  const [period, setPeriod] = useState("today");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Date range for custom filtering
  const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [pendingFromDate, setPendingFromDate] = useState(fromDate);
  const [pendingToDate, setPendingToDate] = useState(toDate);
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [labourData, setLabourData] = useState(null);
  const [paymentBreakdown, setPaymentBreakdown] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    products: true,
    categories: true,
    staff: true,
    hourly: true,
    payments: true,
    transactions: false,
    labour: true,
    trends: true
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  // Debounced fetch for custom date range
  const applyCustomDateRange = useCallback(() => {
    if (period === "custom") {
      setFromDate(pendingFromDate);
      setToDate(pendingToDate);
    }
  }, [period, pendingFromDate, pendingToDate]);
  
  // Fetch all data
  const fetchAllData = useCallback(async (showRefresh = false) => {
    if (showRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build date params
      let dateParams = period === "custom" 
        ? `from_date=${fromDate}&to_date=${toDate}`
        : `period=${period}`;
      
      // Fetch all data in parallel
      const results = await Promise.allSettled([
        fetch(`${API}/pos/reports/dashboard?${dateParams}`, { headers }),
        fetch(`${API}/pos/reports/sales-summary`, { headers }),
        fetch(`${API}/pos/reports/top-products?${dateParams}&limit=15`, { headers }),
        fetch(`${API}/pos/reports/sales-by-category?${dateParams}`, { headers }),
        fetch(`${API}/pos/reports/staff-performance?${dateParams}`, { headers }),
        fetch(`${API}/pos/reports/hourly-sales?${dateParams}`, { headers }),
        fetch(`${API}/pos/clock/labour?from_date=${fromDate}&to_date=${toDate}`, { headers }),
        fetch(`${API}/pos/reports/payment-breakdown?${dateParams}`, { headers }),
        fetch(`${API}/pos/reports/recent-transactions?${dateParams}&limit=20`, { headers }),
        fetch(`${API}/pos/reports/daily-trend?${dateParams}`, { headers }),
      ]);
      
      // Process results
      const [dashRes, summaryRes, productsRes, categoryRes, staffRes, hourlyRes, labourRes, paymentRes, txnRes, trendRes] = results;
      
      if (dashRes.status === 'fulfilled' && dashRes.value.ok) {
        setDashboard(await dashRes.value.json());
      }
      if (summaryRes.status === 'fulfilled' && summaryRes.value.ok) {
        setSalesSummary(await summaryRes.value.json());
      }
      if (productsRes.status === 'fulfilled' && productsRes.value.ok) {
        const data = await productsRes.value.json();
        setTopProducts(data.products || []);
      }
      if (categoryRes.status === 'fulfilled' && categoryRes.value.ok) {
        const data = await categoryRes.value.json();
        setCategoryBreakdown(data.categories || []);
      }
      if (staffRes.status === 'fulfilled' && staffRes.value.ok) {
        const data = await staffRes.value.json();
        setStaffPerformance(data.staff || []);
      }
      if (hourlyRes.status === 'fulfilled' && hourlyRes.value.ok) {
        const data = await hourlyRes.value.json();
        setHourlyData(data.hours || []);
      }
      if (labourRes.status === 'fulfilled' && labourRes.value.ok) {
        setLabourData(await labourRes.value.json());
      }
      if (paymentRes.status === 'fulfilled' && paymentRes.value.ok) {
        const data = await paymentRes.value.json();
        setPaymentBreakdown(data.payments || []);
      }
      if (txnRes.status === 'fulfilled' && txnRes.value.ok) {
        const data = await txnRes.value.json();
        setRecentTransactions(data.transactions || []);
      }
      if (trendRes.status === 'fulfilled' && trendRes.value.ok) {
        const data = await trendRes.value.json();
        setDailyTrend(data.trend || []);
      }
    } catch (err) {
      console.error("Error fetching reports:", err);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, period, fromDate, toDate]);
  
  // Initial load and period change
  useEffect(() => {
    if (period !== "custom") {
      fetchAllData();
    }
  }, [period]);
  
  // Manual refresh for custom dates
  useEffect(() => {
    if (period === "custom" && fromDate && toDate) {
      fetchAllData();
    }
  }, [fromDate, toDate]);
  
  const handleExportTransactions = () => {
    const dateParams = period === "custom" 
      ? `from_date=${fromDate}&to_date=${toDate}`
      : `period=${period}`;
    window.open(`${API}/pos/reports/export/transactions?${dateParams}`, "_blank");
  };
  
  const handleExportProducts = () => {
    const dateParams = period === "custom" 
      ? `from_date=${fromDate}&to_date=${toDate}`
      : `period=${period}`;
    window.open(`${API}/pos/reports/export/products?${dateParams}`, "_blank");
  };
  
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  const formatCompact = (pence) => {
    const pounds = pence / 100;
    if (pounds >= 1000) return `£${(pounds / 1000).toFixed(1)}k`;
    return `£${pounds.toFixed(0)}`;
  };
  
  const periodLabels = {
    today: "Today",
    yesterday: "Yesterday",
    week: "This Week",
    month: "This Month",
    custom: "Custom Range"
  };
  
  // Find max value for bar chart scaling
  const maxHourlySales = Math.max(...hourlyData.map(h => h.sales), 1);
  const maxDailySales = Math.max(...dailyTrend.map(d => d.sales), 1);
  
  // Payment method icons
  const paymentIcons = {
    card: <CreditCard className="h-4 w-4" />,
    cash: <Banknote className="h-4 w-4" />,
    mobile: <Smartphone className="h-4 w-4" />,
    other: <Receipt className="h-4 w-4" />
  };
  
  // Section header component
  const SectionHeader = ({ title, icon, section, actions }) => (
    <div 
      className="flex items-center justify-between mb-4 cursor-pointer"
      onClick={() => toggleSection(section)}
    >
      <h3 className="font-semibold flex items-center gap-2">
        {icon}
        {title}
      </h3>
      <div className="flex items-center gap-2">
        {actions}
        {expandedSections[section] ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </div>
    </div>
  );
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading reports...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header - Brand Gradient */}
        <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Link to="/merchant/pos" className="text-white/80 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
                  <BarChart3 className="h-6 w-6 text-fuchsia-200" />
                  Sales Reports
                </h1>
                <p className="text-indigo-100 text-sm mt-1">
                  {periodLabels[period]} • {dashboard?.total_transactions || 0} transactions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-40 bg-white/20 border-white/30 text-white" data-testid="period-select">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              {period === "custom" && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={pendingFromDate}
                    onChange={(e) => setPendingFromDate(e.target.value)}
                    className="w-36 bg-white/20 border-white/30 text-white [color-scheme:dark]"
                    data-testid="from-date-input"
                  />
                  <span className="text-white/70">to</span>
                  <Input
                    type="date"
                    value={pendingToDate}
                    onChange={(e) => setPendingToDate(e.target.value)}
                    className="w-36 bg-white/20 border-white/30 text-white [color-scheme:dark]"
                    data-testid="to-date-input"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={applyCustomDateRange}
                    className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                    data-testid="apply-date-btn"
                  >
                    <Filter className="h-4 w-4 mr-1" />
                    Apply
                  </Button>
                </div>
              )}
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => fetchAllData(true)}
                disabled={refreshing}
                className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              </Button>
              <Button variant="outline" className="bg-white text-indigo-700 hover:bg-indigo-50 border-0" onClick={handleExportTransactions}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-indigo-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="h-5 w-5 text-indigo-600" />
              {dashboard?.sales_change !== undefined && (
                <span className={`text-xs flex items-center gap-1 ${dashboard.sales_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {dashboard.sales_change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {Math.abs(dashboard.sales_change)}%
                </span>
              )}
            </div>
            <p className="text-3xl font-bold">{formatPrice(dashboard?.total_sales || 0)}</p>
            <p className="text-sm text-gray-400 mt-1">Total Sales ({periodLabels[period]})</p>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl border border-emerald-500/20 p-6">
            <div className="flex items-center justify-between mb-2">
              <ShoppingCart className="h-5 w-5 text-emerald-400" />
              {dashboard?.transactions_change !== undefined && (
                <span className={`text-xs flex items-center gap-1 ${dashboard.transactions_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {dashboard.transactions_change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {Math.abs(dashboard.transactions_change)}%
                </span>
              )}
            </div>
            <p className="text-3xl font-bold">{dashboard?.total_transactions || 0}</p>
            <p className="text-sm text-gray-400 mt-1">Transactions</p>
          </div>
          
          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl border border-amber-500/20 p-6">
            <div className="flex items-center justify-between mb-2">
              <Receipt className="h-5 w-5 text-amber-400" />
            </div>
            <p className="text-3xl font-bold">{formatPrice(dashboard?.average_transaction || 0)}</p>
            <p className="text-sm text-gray-400 mt-1">Avg Transaction</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl border border-purple-500/20 p-6">
            <div className="flex items-center justify-between mb-2">
              <Package className="h-5 w-5 text-purple-400" />
            </div>
            <p className="text-3xl font-bold">{dashboard?.total_items || 0}</p>
            <p className="text-sm text-gray-400 mt-1">Items Sold</p>
          </div>
        </div>
        
        {/* Quick Summary */}
        {salesSummary && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            {Object.entries(salesSummary).map(([key, data]) => (
              <div key={key} className="bg-white/50 rounded-xl border border-gray-200 p-4">
                <p className="text-sm text-gray-400 capitalize">{key.replace("_", " ")}</p>
                <p className="text-xl font-bold text-indigo-400">{formatPrice(data.total_sales)}</p>
                <p className="text-xs text-gray-500">{data.total_transactions} orders</p>
              </div>
            ))}
          </div>
        )}
        
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top Products */}
          <div className="bg-white/50 rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Package className="h-5 w-5 text-indigo-400" />
                Top 10 Products
              </h3>
              <Button variant="ghost" size="sm" onClick={handleExportProducts} className="text-gray-400">
                <Download className="h-4 w-4" />
              </Button>
            </div>
            
            {topProducts.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No sales data for this period</p>
            ) : (
              <div className="space-y-3">
                {topProducts.map((prod, idx) => (
                  <div key={prod._id || idx} className="flex items-center gap-4">
                    <span className="text-gray-500 w-6 text-right">#{idx + 1}</span>
                    <div className="flex-1">
                      <p className="font-medium truncate">{prod.name}</p>
                      <p className="text-xs text-gray-500">{prod.sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-400">{formatPrice(prod.revenue)}</p>
                      <p className="text-xs text-gray-500">{prod.quantity} sold</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Sales by Category */}
          <div className="bg-white/50 rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <PieChart className="h-5 w-5 text-indigo-400" />
              Sales by Category
            </h3>
            
            {categoryBreakdown.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No sales data for this period</p>
            ) : (
              <div className="space-y-3">
                {categoryBreakdown.map((cat, idx) => {
                  const totalRevenue = categoryBreakdown.reduce((sum, c) => sum + c.revenue, 0);
                  const percentage = totalRevenue > 0 ? (cat.revenue / totalRevenue * 100).toFixed(1) : 0;
                  
                  return (
                    <div key={cat._id || idx}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: cat.color || "#6366f1" }}
                          />
                          {cat.category_name}
                        </span>
                        <span className="text-emerald-400">{formatPrice(cat.revenue)}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: cat.color || "#6366f1"
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {percentage}% • {cat.quantity} items
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Staff Performance */}
          <div className="bg-white/50 rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <UserCheck className="h-5 w-5 text-indigo-400" />
              Staff Performance
            </h3>
            
            {staffPerformance.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No staff sales for this period</p>
            ) : (
              <div className="space-y-4">
                {staffPerformance.map((staff, idx) => (
                  <div key={staff._id || idx} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                      <Users className="h-5 w-5 text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{staff.staff_name}</p>
                      <p className="text-xs text-gray-500 capitalize">{staff.pos_role}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-400">{formatPrice(staff.total_sales)}</p>
                      <p className="text-xs text-gray-500">{staff.transaction_count} sales</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Hourly Sales Chart */}
          <div className="bg-white/50 rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5 text-indigo-400" />
              Sales by Hour (Today)
            </h3>
            
            <div className="h-48 flex items-end gap-1">
              {hourlyData.map((hour) => {
                const height = maxHourlySales > 0 ? (hour.sales / maxHourlySales * 100) : 0;
                const isBusinessHour = hour.hour >= 9 && hour.hour <= 21;
                
                return (
                  <div
                    key={hour.hour}
                    className="flex-1 flex flex-col items-center gap-1"
                    title={`${hour.label}: ${formatPrice(hour.sales)} (${hour.transactions} orders)`}
                  >
                    <div
                      className={`w-full rounded-t transition-all ${
                        hour.sales > 0
                          ? "bg-indigo-500"
                          : isBusinessHour
                          ? "bg-slate-700"
                          : "bg-gray-100"
                      }`}
                      style={{ height: `${Math.max(height, 2)}%` }}
                    />
                    {hour.hour % 3 === 0 && (
                      <span className="text-[10px] text-gray-500">{hour.hour}</span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 text-center mt-2">Hour of day (24h)</p>
          </div>
          
          {/* Payment Methods Breakdown */}
          <div className="bg-white/50 rounded-xl border border-gray-200 p-6">
            <SectionHeader 
              title="Payment Methods" 
              icon={<CreditCard className="h-5 w-5 text-indigo-400" />}
              section="payments"
            />
            
            {expandedSections.payments && (
              paymentBreakdown.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No payment data for this period</p>
              ) : (
                <div className="space-y-3">
                  {paymentBreakdown.map((payment, idx) => {
                    const methodName = payment.payment_method?.charAt(0).toUpperCase() + payment.payment_method?.slice(1) || 'Other';
                    return (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                          {paymentIcons[payment.payment_method] || paymentIcons.other}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{methodName}</p>
                          <p className="text-xs text-gray-500">{payment.transaction_count} transactions</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-emerald-500">{formatPrice(payment.total_amount)}</p>
                          <p className="text-xs text-gray-500">{payment.percentage}%</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </div>
        
        {/* Daily Sales Trend */}
        {dailyTrend.length > 1 && (
          <div className="bg-white/50 rounded-xl border border-gray-200 p-6 mt-6">
            <SectionHeader 
              title="Daily Sales Trend" 
              icon={<TrendingUp className="h-5 w-5 text-emerald-400" />}
              section="trends"
            />
            
            {expandedSections.trends && (
              <div className="h-48 flex items-end gap-1">
                {dailyTrend.map((day, idx) => {
                  const height = maxDailySales > 0 ? (day.sales / maxDailySales * 100) : 0;
                  const dateLabel = new Date(day.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                  
                  return (
                    <div
                      key={idx}
                      className="flex-1 flex flex-col items-center gap-1 group"
                      title={`${dateLabel}: ${formatPrice(day.sales)} (${day.transactions} orders)`}
                    >
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-emerald-500 to-emerald-400 transition-all hover:from-emerald-600 hover:to-emerald-500"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                      {(dailyTrend.length <= 14 || idx % Math.ceil(dailyTrend.length / 7) === 0) && (
                        <span className="text-[9px] text-gray-500 -rotate-45 origin-top-left whitespace-nowrap">
                          {dateLabel}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Recent Transactions */}
        <div className="bg-white/50 rounded-xl border border-gray-200 p-6 mt-6">
          <SectionHeader 
            title={`Recent Transactions (${recentTransactions.length})`}
            icon={<Table2 className="h-5 w-5 text-indigo-400" />}
            section="transactions"
            actions={
              <Button variant="ghost" size="sm" onClick={handleExportTransactions} className="text-gray-400">
                <Download className="h-4 w-4" />
              </Button>
            }
          />
          
          {expandedSections.transactions && (
            recentTransactions.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No transactions for this period</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Transaction</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Time</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Items</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Payment</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.map((txn) => {
                      const completedAt = txn.completed_at ? new Date(txn.completed_at) : null;
                      const timeStr = completedAt ? completedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-';
                      const dateStr = completedAt ? completedAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-';
                      const paymentMethod = txn.payments?.[0]?.method || 'N/A';
                      const itemsPreview = txn.items?.slice(0, 2).map(i => i.name).join(', ') || '-';
                      
                      return (
                        <tr key={txn.transaction_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <p className="font-medium text-indigo-600">{txn.transaction_number}</p>
                            {txn.customer_name && (
                              <p className="text-xs text-gray-500">{txn.customer_name}</p>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <p>{timeStr}</p>
                            <p className="text-xs text-gray-500">{dateStr}</p>
                          </td>
                          <td className="py-3 px-2 max-w-[200px]">
                            <p className="truncate">{itemsPreview}</p>
                            {txn.items?.length > 2 && (
                              <p className="text-xs text-gray-500">+{txn.items.length - 2} more</p>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs">
                              {paymentIcons[paymentMethod] || paymentIcons.other}
                              {paymentMethod}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right font-semibold text-emerald-500">
                            {formatPrice(txn.totals?.total || 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        {/* Labour Sheet */}
        <div className="bg-white/50 rounded-xl border border-gray-200 p-6 mt-6">
          <SectionHeader 
            title="Labour Sheet" 
            icon={<Briefcase className="h-5 w-5 text-amber-400" />}
            section="labour"
            actions={
              labourData && (
                <span className="text-xs text-gray-400">
                  {fromDate} {fromDate !== toDate && `- ${toDate}`}
                </span>
              )
            }
          />
          
          {expandedSections.labour && (
            labourData ? (
            <div className="space-y-6">
              {/* Labour Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-gray-100/50 border border-gray-200">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                    <Clock className="h-4 w-4" />
                    Total Hours
                  </div>
                  <p className="text-2xl font-bold">{labourData.summary?.total_hours || 0}h</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-100/50 border border-gray-200">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                    <DollarSign className="h-4 w-4" />
                    Labour Cost
                  </div>
                  <p className="text-2xl font-bold text-amber-400">£{labourData.summary?.total_labour_cost?.toFixed(2) || 0}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-100/50 border border-gray-200">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                    <BarChart3 className="h-4 w-4" />
                    Labour vs Sales
                  </div>
                  <p className="text-2xl font-bold">
                    {dashboard?.total_sales > 0 
                      ? ((labourData.summary?.total_labour_cost / (dashboard.total_sales / 100)) * 100).toFixed(1)
                      : 0}%
                  </p>
                </div>
              </div>

              {/* Staff Breakdown */}
              {labourData.by_staff && labourData.by_staff.length > 0 ? (
                <div>
                  <h3 className="text-sm text-gray-400 mb-3">By Staff Member</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 text-xs text-gray-400 font-medium">Staff</th>
                          <th className="text-right py-3 text-xs text-gray-400 font-medium">Days</th>
                          <th className="text-right py-3 text-xs text-gray-400 font-medium">Hours</th>
                          <th className="text-right py-3 text-xs text-gray-400 font-medium">Rate/hr</th>
                          <th className="text-right py-3 text-xs text-gray-400 font-medium">Total Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {labourData.by_staff.map((staff) => (
                          <tr key={staff.staff_id} className="border-b border-white/5">
                            <td className="py-3">
                              <p className="font-medium">{staff.name}</p>
                            </td>
                            <td className="py-3 text-right text-gray-400">{staff.days_worked}</td>
                            <td className="py-3 text-right">{staff.total_hours}h</td>
                            <td className="py-3 text-right text-gray-400">£{staff.hourly_rate?.toFixed(2) || "0.00"}</td>
                            <td className="py-3 text-right font-medium text-amber-400">£{staff.total_cost?.toFixed(2) || "0.00"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No labour data for this period</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Loading labour data...</p>
            </div>
          )
          )}
        </div>
      </div>
    </div>
  );
};

export default POSReportsPage;
